/**
 * Centralized build-time feature flags.
 *
 * Flags are injected from `REACT_APP_*` env vars (CRA reads them
 * automatically; RSBuild requires each one to be listed in the `define`
 * block of `rsbuild.config.ts`). Keep every flag read here so menu, routes
 * and components agree on the same value.
 */
import { configuredCustomerApiUrl } from "./customerApiUrl";
import { isProductionEnvironment } from "@shared/navigation/SideMenu/sideMenuEnv";
import { isDemoClient } from "@shared/utils/clientEnv";

/**
 * Tags Manager (centralized Tags + TaggedResources pivot).
 *
 * When disabled, the Tags Manager page/menu entry and the rich resource tag
 * picker are hidden and the legacy free-text tag flow is used instead, so the
 * new tagging can coexist with the legacy one. Mirrors the backend
 * `TAGS_MANAGER_ENABLED` flag.
 *
 * Resolution order (so it can be controlled WITHOUT Bitbucket access):
 *   1. Explicit override — `REACT_APP_ENABLE_TAGS_MANAGER=true|false` always
 *      wins (kill-switch / force-on, set in `.env.local` or a deployment var).
 *   2. No flag set → enabled in every NON-production build (local, dev,
 *      staging / `develop`) and disabled in production. This keys off
 *      `REACT_APP_ENV` (which the pipeline sets reliably: `staging` for
 *      `develop`, `production` for prod builds), so the feature ships to
 *      develop/staging by default while prod stays hidden until an explicit
 *      cutover — same pattern other beta features use via
 *      `isProductionEnvironment()`.
 */
export const isTagsManagerEnabled = (): boolean => {
	const raw = (process.env.REACT_APP_ENABLE_TAGS_MANAGER || "")
		.trim()
		.toLowerCase();
	if (raw === "true") return true;
	if (raw === "false") return false;
	return !isProductionEnvironment();
};

/**
 * Subscriptions management (Account → Subscriptions).
 *
 * WIP (CTX-26): the internal subscription model replaced Chargebee as the
 * source of truth, but there is no UI to administer terms and renewals yet.
 * The section ships hidden in production while it is being built and its
 * backing API (CTX-208) lands, and is visible in every NON-production build
 * (local, dev, staging / `develop`).
 *
 * Resolution order mirrors {@link isTagsManagerEnabled} so the rollout can be
 * driven WITHOUT Bitbucket access:
 *   1. Explicit override — `REACT_APP_ENABLE_SUBSCRIPTIONS=true|false` always
 *      wins (kill-switch / force-on, set in `.env.local` or a deployment var).
 *      Remember to register it in `rsbuild.config.ts` `define`, otherwise it
 *      never reaches the bundle.
 *   2. No flag set → enabled outside production, disabled in production.
 *
 * This flag answers "does the feature exist here?". WHO may use it is a
 * separate question owned by `getSubscriptionsAllowedTenantTypes()`.
 *
 * @param isProduction Environment to evaluate against. Callers that already
 * carry the environment — `filterAccountAreaItems` receives it as a
 * parameter — should pass it instead of letting this resolve it again, so the
 * rule can be exercised in tests. `REACT_APP_*` reads are inlined at build
 * time by `rsbuild.config.ts`, which makes the default path impossible to vary
 * at runtime.
 */
export const isSubscriptionsManagerEnabled = (
	isProduction: boolean = isProductionEnvironment()
): boolean => {
	const raw = (process.env.REACT_APP_ENABLE_SUBSCRIPTIONS || "")
		.trim()
		.toLowerCase();
	if (raw === "true") return true;
	if (raw === "false") return false;
	return !isProduction;
};

/**
 * Release Notes / Changelog (`/changelog`).
 *
 * **GA since CTX-616.** It was `!isProductionEnvironment()` from the first
 * commit until the cutover: built, ingested and rendered everywhere except the
 * one environment with customers in it.
 *
 * Whether the feature exists is settled. Who sees it is
 * {@link canSeeChangelog}'s job: since CTX-823 that is every tenant type,
 * after a few weeks of production being `GLOBAL_ADMIN` only (CTX-521).
 *
 * ── Why no `REACT_APP_ENABLE_CHANGELOG` kill-switch ─────────────────────────
 *
 * Because it would not be one. `REACT_APP_*` is inlined into the bundle at
 * **build time** by `source.define`, so flipping the variable costs a rebuild
 * and a redeploy — the exact cost of reverting this line. It would buy nothing
 * operationally while adding a `source.define` entry, a `verify-env-bundle.js`
 * manifest to keep in step, and the migration's most common failure: a flag
 * that silently reads `undefined` because someone added it in one place only.
 *
 * Kept as a function rather than inlined at its call site so the decision still
 * lives in one place. Switching the feed off is editing this return value.
 */
export const isChangelogEnabled = (): boolean => true;

/**
 * Who may see the changelog, on top of whether it exists at all.
 *
 * Source of truth for every changelog surface. There are six — the
 * `/changelog` routes, the footer chip, the What's New card, the modal that
 * card opens, the "New" tags on menu items, and the walkthrough tour the modal
 * launches (`WalkthroughTourProvider`, CTX-510) — and four of them arrived
 * within a week of each other. A gate applied per surface would have been
 * wrong by the next feature, so all of them go through here.
 *
 * The tour is the one that reads least like a changelog surface: it runs on a
 * product screen, not on changelog furniture. It is one anyway — it exists
 * only because an entry announced it, and gating it any other way would let a
 * tenant walk through a change the other five hide from them.
 *
 * ── The rule ────────────────────────────────────────────────────────────────
 *
 * Every tenant type, in every environment (CTX-823). Production was
 * `GLOBAL_ADMIN` only from the CTX-616 cutover until then (CTX-521), a rollout
 * gate so the feed's first weeks in front of real tenants were watched by the
 * people who could fix it. It came off on the evidence in CTX-736.
 *
 * Which *entries* a tenant sees is not decided here and never was: products ×
 * audiences is the server's rule (`GET /api/v2/changelog`, CTX-199, CTX-202),
 * and it filters a CUSTOMER's feed the same whether or not this lets them in.
 * So this is not a security control, and opening it widened no data.
 *
 * ── Why it still takes a tenant type ────────────────────────────────────────
 *
 * The parameter is unused today, and kept on purpose. All six call sites
 * already pass `userProfile.TenantType`, so if the feed ever has to be pulled
 * back from a tenant type, that is one line here rather than six call sites —
 * which is exactly the rollback CTX-823 promises.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept as the rollback seam, see above
export const canSeeChangelog = (_tenantType: string | undefined): boolean =>
	isChangelogEnabled();

/**
 * The `New` tag on the SideMenu rows the latest release names (CTX-511).
 *
 * Off since 2026-10-27, by product decision: the rail stays unmarked. Only the
 * tag is gated — the What's new modal, its walkthroughs and the changelog page
 * are untouched — and while it is off the hook that feeds it fetches nothing.
 * Kept as a named switch so turning it back on is this one line.
 */
export const isMenuNewTagEnabled = (): boolean => false;

/**
 * Customer API tokens (Account → API, `/account/api`).
 *
 * On wherever the deployment environment carries `REACT_APP_CUSTOMER_API_URL`
 * (CTX-708). The page talks to a separate backend, CTX's public API, and only
 * prints its URL; without one it would put a page in front of customers that
 * cannot reach its API. Tying visibility to the variable makes the cutover a
 * configuration change: production shows the page in the build that first
 * carries the URL, and an environment that loses it hides the page again.
 *
 * This is the single predicate behind BOTH surfaces the CTX-36 rule names: the
 * sidemenu row (`filterAccountAreaItems`, on the
 * `account-management-integrations` id, which also drives the header control)
 * and the route registration in `src/settings/routes.tsx`. They were two
 * separate expressions of the same rule, which is how `/account/integrations`
 * stayed reachable by direct URL in production while its menu entry was hidden
 * (CTX-37).
 *
 * @param customerApiUrl The configured base URL; defaults to this build's.
 */
export const isCustomerApiEnabled = (
	customerApiUrl: string | undefined = configuredCustomerApiUrl()
): boolean => customerApiUrl !== undefined;

/**
 * Network Topology (CTX-97) — the source-first
 * Reflector/Cluster -> Cloud -> Region -> Account topology view backed by
 * the CTX-96 `chopper-backend` `/api/v2/insight/*` contract. Lives under
 * Reflector v2.0 -> Management, after BGP Visibility.
 *
 * GA since 2026-08-06: the view ships to every environment, so the gate is a
 * constant rather than an env check. Kept as a named helper (instead of
 * deleting the call sites) so the sidemenu entry and both route trees stay
 * switchable from one place if it ever needs turning off in a hurry.
 */
export const isInsightTopologyV2Enabled = (): boolean => true;

/**
 * Network monitoring (`/kontrol/network-monitoring`, formerly Internet
 * monitoring) — the IPSLA-monitored IPs per backbone uplink and the latency
 * between sites, under Kontrol Edge -> Monitoring.
 *
 * GA since the CTX-339 cutover: the view ships to every environment, so this
 * is a constant rather than an env check. Kept as a named helper instead of
 * deleting the call sites, so the sidemenu row and the route stay switchable
 * from one place if it ever needs turning off in a hurry — same shape as
 * {@link isInsightTopologyV2Enabled}.
 *
 * **This was never the audience rule, and turning it on did not widen one.**
 * Who may open the page is decided by `canAccessKontrolAlerts`, which the row
 * and the route both share: CUSTOMER never, PARTNER and GLOBAL_ADMIN only
 * while standing in the partner's own view rather than one of its customers'.
 * That predicate is untouched here, and it is what makes the cutover
 * partner-only.
 *
 * It is also not the boundary. This runs in the browser, off state the
 * browser owns; the endpoint's own `alertsAccessControl` is what actually
 * refuses a request.
 */
export const isInternetMonitoringEnabled = (): boolean => true;

/**
 * Extended PoP (`/kontrol/extender-pop` + its detail view) — the per-router
 * customer site service (CTX-358..364).
 *
 * BETA: shown in every NON-production build (local, dev, staging) and hidden
 * in production until an explicit cutover — same pattern as
 * `isCustomerApiEnabled`. Gates the sidemenu row, both routes and the
 * Tags Manager wizard entry, so the feature is unreachable in prod even by
 * direct URL.
 */
export const isExtenderPopEnabled = (): boolean => !isProductionEnvironment();

/**
 * Carrier Access (`/kontrol/backhaul-details`, "Backhaul" in the UI) —
 * the last mile as a sellable service with its own `84-` id (P3, CTX-651..654).
 * It has no list or sidemenu row: the CPE and port views link each 84- here.
 *
 * GA since the CTX-1089 cutover: the detail ships to every environment, so
 * this is a constant rather than an env check. Kept as a named helper instead
 * of deleting the call sites — the detail route, its links, the Tags Manager
 * type and the support ticket picker — so they stay switchable from one place
 * if it ever needs turning off in a hurry, same shape as
 * {@link isInternetMonitoringEnabled}.
 *
 * The Extended PoP views stay behind {@link isExtenderPopEnabled}; a Backhaul
 * reaches production through the ports that carry its legs.
 */
export const isCarrierAccessEnabled = (): boolean => true;

/**
 * Carrier Bridge (`/kontrol/carrier-bridge` + its detail view, and the Admin
 * Center's Carrier Bridge and Carriers screens) — the L2 service handed to a
 * carrier over an NNI (P4, CTX-596).
 *
 * BETA until its commercial name and SKU are final (CTX-955): shown in every
 * NON-production build — local, dev, staging, and demo.transitxchange.cloud,
 * which builds as staging on purpose — and hidden in production. Gates the
 * sidemenu row, both customer routes, both Admin Center sections and routes,
 * the Tags Manager wizard entry and the support service picker, so nothing
 * of it is reachable in prod, not even by direct URL. The cutover removes it.
 */
export const isCarrierBridgeEnabled = (): boolean => !isProductionEnvironment();

/**
 * Self-service order flows: Add Port (`/kontrol/add-port`) and Add Internet
 * (`/kontrol/add-ipx`).
 *
 * DEPRECATED: services are ordered through sales, and the flows lost their
 * price source with Chargebee (CTX-42). Hidden in production — no route, so
 * not even a typed URL reaches them; a saved link lands on the service list.
 * Kept in every NON-production build until the flows are removed.
 */
export const isSelfServiceOrderEnabled = (): boolean => !isProductionEnvironment();

/**
 * Kontrol Edge Overview action center (CTX-598, initiative "Overview Refactor").
 *
 * The redesigned `/kontrol` page is the default everywhere; `?layout=classic`
 * opens the classic page instead (ManagerOverviewEntry). Turning this off
 * shows only the classic page.
 */
export const isOverviewActionCenterEnabled = (): boolean => true;

/**
 * Where the single Overview endpoint lives, or `null` to keep the four calls
 * (CTX-868, project "Overview servido por un solo endpoint").
 *
 * The endpoint is read from the same API as everything else
 * (`REACT_APP_API_URL`), so no environment needs a new deployment variable;
 * a backend that does not serve it yet answers 404 and the page falls back to
 * the four calls (useManagerOverviewData). `REACT_APP_OVERVIEW_API_URL`
 * overrides the base for a developer whose endpoint only runs locally — e.g.
 * `http://localhost:3011/api` — while the rest stays on staging.
 */
export const getOverviewEndpointBaseUrl = (): string | null => {
	const raw = (process.env.REACT_APP_OVERVIEW_API_URL || process.env.REACT_APP_API_URL || "")
		.trim()
		.replace(/\/+$/, "");
	return raw || null;
};

/**
 * Base URL of the rebuilt Archi (P-CTX-123, customer-api-docs `apps/archi`),
 * e.g. http://localhost:3100 while it runs locally. Set, the new Archi panel
 * replaces the old FAB and sidebar; empty, the old Archi on chopper-backend
 * stays. One switch for both, so the two never show at once.
 */
export const getArchiServiceUrl = (): string | null => {
	const raw = (process.env.REACT_APP_ARCHI_URL || "").trim().replace(/\/+$/, "");
	return raw || null;
};

/**
 * KE Sales Motion (P-CTX-147) — the client-facing sales module under `/sales-motion`.
 *
 * Decided by the environment, no variable to set: dev, staging and the demo build
 * (`REACT_APP_CLIENT=demo`), never production. Gates the routes and the "Sales Motion"
 * entry of the product selector. Going live in production is a PR that lifts this lock.
 */
export const isSalesMotionEnabled = (): boolean => !isProductionEnvironment() || isDemoClient();

